/** Nature of a stock movement — mirrors `PozStockMovementType` in Core. */
export type StockMovementType =
  | 'sale'
  | 'sale-return'
  | 'reception'
  | 'reception-cancelled'
  | 'adjustment'
  | 'compliance'
  | 'packaging'
  | 'count'
  | 'import';

/** Why a manual adjustment was made — mirrors `PozStockMovementReason`. */
export type StockMovementReason =
  | 'breakage'
  | 'theft'
  | 'expiry'
  | 'data-entry-error'
  | 'gift'
  | 'internal-use'
  | 'supplier-return'
  | 'count-difference'
  | 'other';

/**
 * Built-in reasons offered when correcting a stock by hand, used when the shop
 * has not configured its own list (`pm_config.stockMovementReasons`).
 */
export const STOCK_MOVEMENT_REASONS: StockMovementReason[] = [
  'data-entry-error',
  'breakage',
  'theft',
  'expiry',
  'gift',
  'internal-use',
  'supplier-return',
  'other',
];

export interface PozStockMovement {
  id: number;
  productId: number;
  productVariantId: number | null;
  productName: string | null;
  productReference: string | null;
  type: StockMovementType;
  /** A built-in `StockMovementReason` code, or a free label configured by the shop. */
  reason: string | null;
  /**
   * Gateway shops only: the gateway's own reason name, for movements with no
   * Positize equivalent (e.g. a PrestaShop back-office edit).
   */
  reasonLabel?: string | null;
  quantityBefore: number | null;
  quantityDelta: number;
  quantityAfter: number | null;
  unitCost: number | null;
  batchNumber: string | null;
  serialNumber: string | null;
  sourceType: string | null;
  sourceId: number | null;
  sourceReference: string | null;
  userId: number | null;
  username: string | null;
  userEmail: string | null;
  note: string | null;
  createdDate: string;
}

export class StockMovementCriteria {
  productId?: number;
  /** Pass 0 to restrict to the product itself (no variant). */
  productVariantId?: number;
  type?: string;
  reason?: string;
  userId?: number;
  sourceType?: string;
  sourceId?: number;
  searchString?: string;
  startDate?: Date;
  endDate?: Date;
  page?: number;
  pageSize?: number;
}

export interface StockMovementPage {
  items: PozStockMovement[];
  total: number;
}

/**
 * Display label of a reason. Built-in codes are translated
 * (`StockMovementReason_<code>`); a free label configured by the shop is shown
 * as typed.
 */
export function stockMovementReasonLabel(
  translate: { instant: (key: string) => string },
  reason: string | null | undefined,
): string {
  if (!reason) {
    return '';
  }
  const key = 'StockMovementReason_' + reason;
  const translated = translate.instant(key);
  return translated && translated !== key ? translated : reason;
}
